// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { getBillTrustLink } from "@keller/App/Store/Selectors/GeneralSelectors";
import { getIsNonPurchasable } from "@keller/App/Store/Selectors/GeneralSelectors";
import Billtrust from "@keller/XDL/Icons/Billtrust";
import { iconLinkCommonStyles, mergeWithCss, xScreen } from "@keller/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    showAs = "showAs",
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showAs]: "link" | "button";
        [fields.title]: string;
    };
}

interface BillTrustStyles {
    link?: LinkPresentationProps;
    button?: ButtonPresentationProps;
}

const linkStyle: LinkPresentationProps = {
    icon: {
        iconProps: {
            size: 24,
            css: css`
                ${xScreen("lte", "md")} {
                    margin-right: auto;
                    margin-left: auto;
                }
            `,
        },
    },
    typographyProps: {
        css: css`
            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
    css: css`
        ${xScreen("lte", "md")} {
            min-width: 44px;
            height: 44px;
        }
    `,
};

const billTrustStyles: BillTrustStyles = {
    link: {
        ...mergeWithCss(iconLinkCommonStyles(Billtrust), linkStyle),
    },
};

const styles = billTrustStyles;
const BillTrust: FC<OwnProps> = ({ fields: { showAs, title } }) => {
    const billTrustLink = useAppSelector(state => getBillTrustLink(state));
    const isNonPurchasable = useAppSelector(state => getIsNonPurchasable(state));

    if (!billTrustLink) {
        return null;
    }

    const onClickHandler = () => {
        typeof window !== "undefined" && window.open(`${billTrustLink}`);
    };

    if (isNonPurchasable) {
        return null;
    }

    if (showAs === "link") {
        return (
            <Link onClick={onClickHandler} {...styles.link} data-test-selector="bill-trust-link">
                {title}
            </Link>
        );
    }

    return (
        <Button onClick={onClickHandler} {...styles.button} data-test-selector="bill-trust-link">
            {title}
        </Button>
    );
};

const widgetModule: WidgetModule = {
    component: BillTrust,
    definition: {
        group: "Xngage Custom" as any,
        displayName: "BillTrust",
        fieldDefinitions: [
            {
                name: fields.showAs,
                editorTemplate: "DropDownField",
                defaultValue: "link",
                fieldType: "General",
                options: [
                    {
                        displayName: "Link",
                        value: "link",
                    },
                    {
                        displayName: "Button",
                        value: "button",
                    },
                ],
                hideEmptyOption: true,
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "BillTrust",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
